import {ConfirmationDialog, dateTime, DetailColumns, DetailPane, useDialogs, useErrors} from '@management-ui/core';
import {Delete, Drafts, Mail} from '@mui/icons-material';
import * as React from 'react';
import {useCallback, useContext, useEffect, useMemo, useRef, useState} from 'react';
import {useRouteMatch} from 'react-router';
import {useHistory} from 'react-router-dom';
import {ServiceContext} from '../../../../components/Services';
import Text from '../../../../components/Text';
import routes from '../../../../routes';
import Tabs from './Tabs';
import Wrapper from './Wrapper';

const InboxMessage = () => {
  const services = useContext(ServiceContext);
  const history = useHistory();
  const match = useRouteMatch();

  const {errors, toggleError} = useErrors(
    useMemo(() => ({message: 'Sorry the message could not be accessed'}), [])
  );

  const {openDialogs, toggleDialog} = useDialogs(['delete', 'close', 'open']);

  const idRef = useRef('');
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);

  const loadMessage = useCallback((id) => {
    if (idRef.current !== `${id}`) {
      idRef.current = `${id}`;
      setLoading(true);
      services.inbox.getMessage(id).then(retrieved => {
        setMessage(retrieved);
        setLoading(false);
      }).catch(() => {
        toggleError('message', true);
        setLoading(false);
        setTimeout(() => {
          history.push(routes.inbox.index);
        }, 3000);
      });
    }
  }, [services, history, toggleError]);

  useEffect(() => {
    if (match.params?.id) {
      loadMessage(match.params?.id);
    }
  }, [match, message, loadMessage]);

  const [title, setTitle] = useState('Loading');
  const [crumbs, setCrumbs] = useState([...Wrapper.standardCrumbs]);

  useEffect(() => {
    let header = 'Loading';
    let trail = [];
    if (message) {
      header = `${message.sender} - ${dateTime(message.created_at)}`;
      trail = [
        ...Wrapper.standardCrumbs,
        {title: header, link: ''}
      ];
    }
    setTitle(header);
    setCrumbs(trail);
  }, [message]);

  const handleToggle = useCallback((confirmed) => {
    toggleDialog('toggle', false);
    if (confirmed) {
      setLoading(true);
      services.inbox.toggleMessage(message).then((toggled) => {
        setLoading(false);
        setMessage(toggled)
      }).catch(() => setLoading(false));
    }
  }, [services, toggleDialog, message]);

  const handleDelete = useCallback((confirmed) => {
    toggleDialog('delete', false);
    if (confirmed) {
      setLoading(true);
      services.inbox.deleteMessage(message).then(() => {
        setLoading(false);
        history.push(routes.inbox.index);
      }).catch(() => setLoading(false));
    }
  }, [services, history, toggleDialog, message]);

  return (
    <Wrapper title={title} message={message} loading={loading} crumbs={crumbs} errors={errors}>
      {message?.id ? (
        <>
          <DetailColumns columns={[

            <DetailPane
              title="Message Details"
              actions={[
                ...(message.closed_at ? [
                  {title: 'Open', icon: <Drafts/>, onClick: () => toggleDialog('toggle', true)},
                ] : [
                  {title: 'Close', icon: <Mail/>, onClick: () => toggleDialog('toggle', true)},
                ]),
                {title: 'Delete', icon: <Delete/>, onClick: () => toggleDialog('delete', true)},
              ]}
              details={[
                {title: 'Sender', value: message.sender},
                {title: 'Subject', value: message.subject},
                {title: 'Body', value: <Text text={message.body}/>},
              ]}
              dialogs={[
                (props) => (
                  <ConfirmationDialog
                    {...props}
                    open={openDialogs['delete'] ?? false}
                    title="Delete Message"
                    message="Are you sure you want to delete this message?"
                    onClose={handleDelete}
                  />
                ),
                (props) => (
                  <ConfirmationDialog
                    {...props}
                    open={openDialogs['toggle'] ?? false}
                    title={message.closed_at ? 'Open Message' : 'Close Message'}
                    message={`Are you sure you want to ${message.closed_at ? 'open' : 'close'} this message?`}
                    onClose={handleToggle}
                  />
                )
              ]}
            />,

            <DetailPane
              title="Admin Details"
              details={[
                {title: 'Created', value: dateTime(message.created_at)},
                {title: 'Last Updated', value: dateTime(message.updated_at)},
                {title: 'Closed', value: message.closed_at ? dateTime(message.closed_at) : '-'}
              ]}
            />,
          ]}/>

          <Tabs message={message} onMessageUpdated={setMessage}/>
        </>
      ) : null}
    </Wrapper>
  );
};

export default InboxMessage;
